import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import type {
  CalendarConversationType,
  CalendarEventType,
  User,
  BaseCandidateOpportunity,
  Opportunity,
  ClientRequest,
} from '@axiom/validation';
import type { CleanEvent } from '@axiom/ui';
import {
  CondensedHeader,
  Grid,
  GridColumn,
  GridRow,
  Layout,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  SmallHeader,
  Calendar,
  CalendarPermissionsUtil,
  Button,
  LayoutItem,
  Gutter,
  Form,
  CalendarTimeZoneUtil,
  CondensedMedium,
  useBreakpoint,
  CalendarEventsUtil,
  EVENT_STATES,
  Badge,
  Paragraph,
  CalendarLegendKey,
  TimezoneDropdown,
} from '@axiom/ui';
import { PermissionImpersonationRoles } from '@axiom/const';

import { CalendarApi } from '../../api/calendar-api';
import { CalendarConversationsApi } from '../../api/calendar-conversations-api';
import { OpportunityStore } from '../../stores/opportunity-store';
import { useRefreshCandidate } from '../../hooks/useRefreshCandidate';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import type { InterviewCandidateType } from '../../hooks/useInterviewAvailabilityCandidateData';
import { InterviewAttendeesAndLinkModalCreate } from '../InterviewAttendeesAndLinkModalCreate/InterviewAttendeesAndLinkModalCreate';
import { TalentTimezoneEditor } from '../TalentTimezoneEditor/TalentTimezoneEditor';

import { useTalentInterviewAvailabilityModalData } from './useTalentInterviewAvailabilityModalData';
import { useCanDoInterviewEvents } from './useCanDoInterviewEvents';
import type { CalendarScheduleModeValueType } from './talent-interview-availability-modal.const';
import type { TalentInterviewAvailabilityModalType } from './talent-interview-availability-modal-schema';
import { TalentInterviewAvailabilityModalSchema } from './talent-interview-availability-modal-schema';
import {
  FifteenMinMode,
  FiveMinMode,
  CalendarScheduleModeOptions,
  CalendarScheduleModes,
} from './talent-interview-availability-modal.const';

export type TalentInterviewAvailabilityModalProps = {
  name?: string;
  candidate: InterviewCandidateType;
  editableEventId?: string | null;
  // Required when editing the time of a saved interview. The submit
  // handler patches the conversation's startTime/endTime instead of
  // patching the event row directly so all sibling events (and the
  // grid's display time) stay in sync via the existing CDC handler.
  editableConversationId?: CalendarConversationType['id'] | null;
  user: User;
  onClose: (addDelay?: boolean) => void;
  defaultMode?: CalendarScheduleModeValueType;
  opportunityCandidateStatus?: BaseCandidateOpportunity['candidateStatus'];
  opportunityId?: Opportunity['id'];
  clientRequestId?: ClientRequest['id'];
};

export const TalentInterviewAvailabilityModal = ({
  name,
  candidate,
  editableEventId,
  editableConversationId,
  user,
  onClose,
  defaultMode = CalendarScheduleModes.AVAILABILITY,
  opportunityCandidateStatus,
  opportunityId,
  clientRequestId,
}: TalentInterviewAvailabilityModalProps) => {
  const guessIana = moment.tz.guess();
  const { isMobile } = useBreakpoint();
  const [scheduledEvents, setScheduledEvents] = useState<CleanEvent[]>([]);
  const [showAttendeeStep, setShowAttendeeStep] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [savedTalentIana, setSavedTalentIana] = useState<string | undefined>();
  const activeTimeMode = isZoomedIn ? FiveMinMode : FifteenMinMode;
  const calendarHeight = isZoomedIn ? '1800px' : '600px';

  // react-big-calendar measures column widths once and only re-measures on
  // window resize. Toggling 5-min mode swaps the calendar height between
  // 600px and 1800px, which leaves columns mis-aligned until the next real
  // window resize. Fire a synthetic resize after the height prop has
  // committed so RBC re-runs its layout math.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [isZoomedIn]);
  const { refreshCandidate } = useRefreshCandidate(candidate.id);
  const {
    changePermissionsToEditStaticEvent: changeInterviewPermissions,
    changePermissionsToEditEventBoundary: changeFreeTimePermissions,
  } = CalendarPermissionsUtil(user, PermissionImpersonationRoles.envoy);
  const calendarId = candidate.calendar?.id ?? null;
  // savedTalentIana reflects a timezone saved during this session (the editor
  // updates it locally — see below); otherwise fall back to the candidate prop.
  const talentIana = savedTalentIana ?? candidate.calendar?.timezone;
  const talentTimezone = CalendarTimeZoneUtil.getTimeZone(
    talentIana || guessIana
  );
  const [displayTimezone, setDisplayTimezone] = useState(talentTimezone);
  const timezone = displayTimezone;
  const { opportunity, position, calendarData, candidateOppData } =
    useTalentInterviewAvailabilityModalData(candidate, opportunityId);

  const formInitData = useMemo(() => {
    const { freeTimes, interviewTimes } = (calendarData?.events || [])
      .filter(
        (event: CalendarEventType) =>
          // Always include the event being edited, even if it would
          // otherwise be filtered as past. Otherwise the modal opens with
          // the editable event invisible and its anchor week / scroll
          // position can't be computed.
          event.id === editableEventId ||
          !CalendarEventsUtil.isPastEnded(event.endTime)
      )
      .reduce(
        (crnt, item) => {
          item.state = item.state ?? EVENT_STATES.SAVED;

          if (item.busy) {
            crnt.interviewTimes.push(item);
          } else {
            crnt.freeTimes.push(item);
          }

          return crnt;
        },
        {
          freeTimes: [] as CalendarEventType[],
          interviewTimes: [] as CalendarEventType[],
        }
      );

    const data: TalentInterviewAvailabilityModalType = {
      CALENDAR_COMPONENT: [],
      BACKGROUND_EVENTS: [],
      DISPLAY_TIMEZONE: talentTimezone.id,
      MODE_CHANGER: defaultMode,
    };

    if (defaultMode === CalendarScheduleModes.AVAILABILITY) {
      data.CALENDAR_COMPONENT = freeTimes;
      data.BACKGROUND_EVENTS = interviewTimes;
    } else {
      // Re-seed any drafted-but-unscheduled interview slots so they reappear
      // on the calendar when the user navigates BACK from the attendee step.
      data.CALENDAR_COMPONENT = [
        ...interviewTimes,
        ...scheduledEvents.map(event => ({
          ...event,
          id: CalendarEventsUtil.generateEventId(
            new Date(event.startTime),
            new Date(event.endTime)
          ),
          state: EVENT_STATES.NEW,
        })),
      ];
      data.BACKGROUND_EVENTS = freeTimes;
    }

    return data;
  }, [calendarData?.events, defaultMode, editableEventId, scheduledEvents]);

  // When opening the modal in edit-interview-time mode, anchor the calendar
  // on the week containing the editable event so the user lands on the
  // right week regardless of what day-of-week "today" happens to be.
  const editableEventStart = useMemo(() => {
    if (!editableEventId) return;
    const event = formInitData.CALENDAR_COMPONENT.find(
      e => e.id === editableEventId
    );
    return event?.startTime ? new Date(event.startTime) : undefined;
  }, [editableEventId, formInitData]);

  const editableScrollToTime = useMemo(() => {
    if (!editableEventStart) return;
    return moment(editableEventStart).subtract(1, 'hour').toDate();
  }, [editableEventStart]);

  const canDoInterviewEvents: boolean = useCanDoInterviewEvents({
    opportunity,
    position,
    user,
    opportunityCandidateStatus,
  });

  const refreshData = async () => {
    await Promise.all([
      CalendarApi.refreshCalendar(calendarId),
      CalendarApi.refreshCalendarEvents(calendarId),
      opportunity?.id ? OpportunityStore.load(opportunity.id) : null,
      refreshCandidate(candidate.id),
    ]);
  };

  const handleClose = (...args: Parameters<typeof onClose>) => {
    onClose(...args);
    if (savedTalentIana && calendarId) {
      void CalendarApi.refreshCalendar(calendarId);
      void refreshCandidate(candidate.id);
    }
  };

  return showAttendeeStep ? (
    <div>
      {opportunity ? (
        <InterviewAttendeesAndLinkModalCreate
          refreshData={refreshData}
          onBackHandler={() => {
            setShowAttendeeStep(false);
          }}
          opportunity={opportunity}
          candidate={candidate}
          onClose={onClose}
          clientRequestId={clientRequestId}
          scheduledEvents={scheduledEvents.sort((a, b) => {
            if (!a.startTime || !b.startTime) {
              return 0;
            }
            return a.startTime > b.startTime ? 1 : -1;
          })}
          timezone={timezone}
        />
      ) : (
        'No Opportunity Available'
      )}
    </div>
  ) : (
    <Form
      name="INTERVIEW_AUTO_FORM"
      schema={TalentInterviewAvailabilityModalSchema}
      initialValues={formInitData}
      onSubmit={async (
        changedData,
        {
          setFieldError,
        }: { setFieldError: (field: string, message: string) => void },
        { CALENDAR_COMPONENT, BACKGROUND_EVENTS, MODE_CHANGER }
      ) => {
        const allEvents = [...CALENDAR_COMPONENT, ...BACKGROUND_EVENTS];

        const activePermissions =
          MODE_CHANGER === CalendarScheduleModeOptions[0].id
            ? changeFreeTimePermissions()
            : changeInterviewPermissions();

        const cleanEvents = CalendarEventsUtil.cleanAndOrganiseEvents({
          permissions: activePermissions,
          initData: calendarData?.events || [],
          changedEvents: allEvents,
        });

        if (!talentIana) {
          setFieldError(
            'CALENDAR_COMPONENT',
            "Set the talent's timezone to proceed"
          );

          return;
        }

        const hasCalendarChanges = Object.values(cleanEvents).flat().length > 0;
        const hasTimezoneChange = 'TIMEZONE_DROPDOWN' in changedData;

        if (!hasCalendarChanges && !hasTimezoneChange) {
          // no event changes or timezone changes - require at least one edit
          setFieldError(
            'CALENDAR_COMPONENT',
            'Please make at least one edit to continue.'
          );

          return;
        }

        let currentCalendarId = calendarId;
        if (!currentCalendarId) {
          const newCalendar = await CalendarApi.createCalendar({
            candidateId: candidate.id,
            timezone: timezone.iana,
          });
          currentCalendarId = newCalendar.data.id;
        }

        const promises = [];

        // These API calls are in specific order to prevent race-conditions ==================== START
        if (cleanEvents.deleteIds.length > 0) {
          await CalendarApi.deleteCalendarEvents(
            currentCalendarId,
            cleanEvents.deleteIds
          );
        }

        if (cleanEvents.modifiedEvents.length > 0) {
          // Edit-interview-time mode: route the change through the
          // conversation-level patch so the conversation's own
          // startTime/endTime stay in sync (and CDC propagates to all
          // events on the conversation). The lock-out logic guarantees
          // only the editable event can be modified, so modifiedEvents
          // has at most one entry here.
          if (editableConversationId && editableEventId) {
            const editedEvent = cleanEvents.modifiedEvents.find(
              event => event.id === editableEventId
            );
            if (editedEvent) {
              promises.push(
                CalendarConversationsApi.updateTime(editableConversationId, {
                  startTime: editedEvent.startTime,
                  endTime: editedEvent.endTime,
                })
              );
            }
          } else {
            const payload = cleanEvents.modifiedEvents.reduce<{
              [key: string]: CleanEvent;
            }>((crnt, event) => {
              const { id, ...evnt } = event;
              if (id) {
                crnt[id] = evnt;
              }
              return crnt;
            }, {});

            promises.push(
              CalendarApi.updateCalendarEvents(currentCalendarId, payload)
            );
          }
        }

        if (cleanEvents.newEvents.length > 0) {
          promises.push(
            CalendarApi.createCalendarEvents(
              currentCalendarId,
              cleanEvents.newEvents
            )
          );
        }

        await Promise.all(promises);
        // These API calls are in specific order to prevent race-conditions ==================== END

        if (cleanEvents.staticEvents.length > 0) {
          setScheduledEvents(cleanEvents.staticEvents);
          setShowAttendeeStep(true);
        } else {
          void refreshData();
          onClose(true);
        }
      }}
    >
      {({ fireSubmit, values, setValues }) => {
        const isAvailabilityMode =
          values.MODE_CHANGER === CalendarScheduleModeOptions[0].id;
        const activePermissions = isAvailabilityMode
          ? changeFreeTimePermissions()
          : changeInterviewPermissions();

        return (
          <Modal size="large" name={name || 'INTERVIEW_AUTO_MODAL'}>
            <ModalHeader
              name="MODAL_HEADER"
              onClose={() => {
                handleClose();
              }}
            >
              <Layout position="middle" wrap>
                <LayoutItem fluid>
                  <SmallHeader name="HEADER_TEXT">
                    {isAvailabilityMode
                      ? `Edit ${candidate.displayName}'s interview availability`
                      : `Interview with ${candidate.displayName}`}
                  </SmallHeader>
                </LayoutItem>
                {canDoInterviewEvents ? (
                  <LayoutItem name="BADGE_CONTAINER" rightGutter="16px">
                    {isAvailabilityMode ? (
                      <Badge name="EDIT_AVAILABILITY" background="blue">
                        Edit availability
                      </Badge>
                    ) : (
                      <Badge
                        name="SCHEDULE_INTERVIEW"
                        background="forest green"
                      >
                        Schedule interview
                      </Badge>
                    )}
                  </LayoutItem>
                ) : null}
              </Layout>
            </ModalHeader>
            <ModalSection>
              <Grid>
                <GridRow>
                  <GridColumn widescreenWidth={4} smallScreenWidth={12}>
                    {isAvailabilityMode ? (
                      <>
                        <CondensedMedium name="TALENTS_AVAILABILITY_TEXT">
                          Choose as many time slots as possible to increase the
                          likelihood of being matched for an interview. We
                          suggest a minimum 4 hours over the next two weeks.
                        </CondensedMedium>
                        <Gutter bottom="24px" />
                      </>
                    ) : null}
                    {!isAvailabilityMode &&
                    candidateOppData?.clientRequest
                      ?.calendarManualInterviewRequests[0]?.availabilityNote ? (
                      <Gutter bottom="24px" name="CLIENT_REQUEST_NOTES">
                        <CondensedHeader name="PENDO_CLIENT_AVAILABILITY">
                          Client availability:
                        </CondensedHeader>
                        <RichTextEditor
                          readOnly
                          initialValue={
                            candidateOppData.clientRequest
                              .calendarManualInterviewRequests[0]
                              .availabilityNote
                          }
                        />
                      </Gutter>
                    ) : null}
                    <TimezoneDropdown
                      name="DISPLAY_TIMEZONE"
                      label="Display time zone"
                      onChange={(id, _e, tz) => {
                        if (tz) {
                          setDisplayTimezone(tz);
                        } else if (id) {
                          setDisplayTimezone(
                            CalendarTimeZoneUtil.getZoneFromId(id)
                          );
                        }
                      }}
                    />
                    <Gutter bottom="16px" />
                    <TalentTimezoneEditor
                      candidateId={candidate.id}
                      calendarId={calendarId}
                      mode={talentIana ? 'edit' : 'set'}
                      currentTimezone={talentIana ? talentTimezone : undefined}
                      onSaved={async iana => {
                        const tz = CalendarTimeZoneUtil.getTimeZone(iana);
                        setDisplayTimezone(tz);
                        await setValues(prev => ({
                          ...prev,
                          DISPLAY_TIMEZONE: tz.id,
                        }));
                        setSavedTalentIana(iana);
                      }}
                    />
                    <Gutter bottom="16px" />
                  </GridColumn>
                  <GridColumn widescreenWidth={8} smallScreenWidth={12}>
                    <Calendar
                      name="CALENDAR_COMPONENT"
                      backgroundEvents={values.BACKGROUND_EVENTS}
                      editableEventId={editableEventId}
                      permissions={activePermissions}
                      dayMode={activeTimeMode}
                      weekMode={activeTimeMode}
                      height={calendarHeight}
                      timezone={timezone.iana}
                      displayDate={editableEventStart}
                      scrollToTime={editableScrollToTime}
                      toolbarLeftSlot={
                        <Button
                          name="ZOOM_TOGGLE"
                          pattern="secondary"
                          variation="outline"
                          onClick={() => {
                            setIsZoomedIn(prev => !prev);
                          }}
                        >
                          {isZoomedIn ? '15 min' : '5 min'}
                        </Button>
                      }
                    />
                    <Gutter bottom="8px" />
                    <Layout position="left middle" horizontalGutter="8px">
                      <LayoutItem>
                        <CalendarLegendKey type="availability" />
                      </LayoutItem>
                      <LayoutItem rightGutter="16px">
                        <Paragraph name="LEGEND_TEXT_AVAILABILITY">
                          {candidate.displayName}'s availability
                        </Paragraph>
                      </LayoutItem>
                      <LayoutItem>
                        <CalendarLegendKey type="scheduled" />
                      </LayoutItem>
                      <LayoutItem rightGutter="16px">
                        <Paragraph name="LEGEND_TEXT">
                          Scheduled Interviews
                        </Paragraph>
                      </LayoutItem>
                      {isAvailabilityMode ? null : (
                        <>
                          <LayoutItem>
                            <CalendarLegendKey type="new-interview" />
                          </LayoutItem>
                          <LayoutItem>
                            <Paragraph name="LEGEND_TEXT_NEW_INTERVIEW">
                              {editableEventId
                                ? 'Editable interview'
                                : 'New interview'}
                            </Paragraph>
                          </LayoutItem>
                        </>
                      )}
                    </Layout>
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                onClick={() => {
                  handleClose();
                }}
                pattern="primary"
                variation="outline"
                name="CANCEL_BUTTON"
              >
                Cancel
              </Button>
              {!isMobile &&
                (() => {
                  const isEditingInterviewTime = editableEventId != null;
                  const hasUnsavedFutureBusyEvent =
                    values.CALENDAR_COMPONENT?.find(
                      event =>
                        !CalendarEventsUtil.isInThePast(
                          event.endTime,
                          activePermissions
                        ) &&
                        event.busy &&
                        event.state !== EVENT_STATES.SAVED
                    );
                  const showSave =
                    isEditingInterviewTime ||
                    (isAvailabilityMode && !hasUnsavedFutureBusyEvent);

                  return showSave ? (
                    <Button onClick={fireSubmit} name="SAVE_BUTTON">
                      Save
                    </Button>
                  ) : (
                    <Button onClick={fireSubmit} name="NEXT_BUTTON">
                      Next
                    </Button>
                  );
                })()}
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};

export { CalendarScheduleModes } from './talent-interview-availability-modal.const';
